import { supabaseClient } from './supabase';

interface ParsedContent {
  headers: {
    h1: string[];
    h2: string[];
    h3: string[];
    h4: string[];
  };
  texts: string[];
  word_count: number;
  orderedContent?: Array<{ type: 'header' | 'text', level?: string, content: string }>;
}

function cleanText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/[^\p{L}\p{N}\s\-.,?!()«»""'']/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractHeaderText(text: string): string {
  const cleaned = cleanText(text);
  const parts = cleaned.split(/[.!?]/).filter(Boolean);
  
  if (parts.length > 0) {
    const firstPart = parts[0].trim();
    if (firstPart.length >= 3) {
      return firstPart;
    }
  }
  
  return cleaned;
}

function isValidHeader(text: string): boolean {
  if (!text || text.length < 3 || text.length > 200) return false;
  if (!/\p{L}/u.test(text)) return false;
  
  const navigationWords = ['menu', 'navigation', 'skip', 'main content', 'search'];
  if (navigationWords.some(word => text.toLowerCase().includes(word))) return false;
  
  return true;
}

async function fetchWithRetry(url: string, maxRetries = 3): Promise<string> {
  const proxyUrls = [
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://corsproxy.io/?${encodeURIComponent(url)}`,
    `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`
  ];

  let lastError;
  for (let retry = 0; retry < maxRetries; retry++) {
    for (const proxyUrl of proxyUrls) {
      try {
        const response = await fetch(proxyUrl, {
          headers: {
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
          },
          signal: AbortSignal.timeout(10000)
        });
        
        if (!response.ok) {
          console.warn(`Proxy ${proxyUrl} returned status ${response.status}`);
          continue;
        }
        
        const html = await response.text();
        
        if (html.length > 500 && 
            (html.includes('</html>') || html.includes('</body>'))) {
          return html;
        }
        
        console.warn(`Proxy ${proxyUrl} returned invalid content length: ${html.length}`);
      } catch (error) {
        lastError = error;
        console.warn(`Proxy ${proxyUrl} failed:`, error);
        continue;
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 1000 * (retry + 1)));
  }

  throw lastError || new Error('Failed to fetch content after all retries');
}

async function parseUrl(url: string): Promise<ParsedContent> {
  try {
    console.log('Fetching URL:', url);
    const html = await fetchWithRetry(url);
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove unwanted elements
    const unwantedSelectors = [
      'script', 'style', 'noscript', 'iframe', 'nav', 'footer', 
      'header', 'aside', '.menu', '.navigation', '.sidebar'
    ];
    
    unwantedSelectors.forEach(selector => {
      doc.querySelectorAll(selector).forEach(el => el.remove());
    });

    // Extract content in document order (ONLY for orderedContent field)
    const orderedContent: Array<{ type: 'header' | 'text', level?: string, content: string }> = [];
    
    // Extract headers and texts separately (for analysis)
    const texts: string[] = [];
    const headers = {
      h1: [] as string[],
      h2: [] as string[],
      h3: [] as string[],
      h4: [] as string[]
    };

    // First, extract headers separately for analysis
    ['h1', 'h2', 'h3', 'h4'].forEach(tag => {
      doc.querySelectorAll(tag).forEach(el => {
        const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
        if (style && (style.display === 'none' || style.visibility === 'hidden')) {
          return;
        }
        
        // Remove nested header tags
        el.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(nested => nested.remove());
        const headerText = cleanText(el.textContent || '');
        
        if (isValidHeader(headerText)) {
          headers[tag as keyof typeof headers].push(headerText);
        }
      });
    });

    // Extract texts separately for analysis
    // Get text from paragraphs
    doc.querySelectorAll('p').forEach(el => {
      const text = cleanText(el.textContent || '');
      if (text.length >= 20) {
        texts.push(text);
      }
    });

    // If no paragraphs found, try getting text from divs and other elements
    if (texts.length === 0) {
      doc.querySelectorAll('div, article, section, main').forEach(el => {
        if (!el.querySelector('div, article, section, main, p')) {
          const text = cleanText(el.textContent || '');
          if (text.length >= 20) {
            texts.push(text);
          }
        }
      });
    }

    // NOW extract ordered content for editor (separate process)
    const allElements = doc.querySelectorAll('h1, h2, h3, h4, p, div, article, section, main');
    
    allElements.forEach(el => {
      const tagName = el.tagName.toLowerCase();
      
      // Check if element is visible
      const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden')) {
        return;
      }

      if (['h1', 'h2', 'h3', 'h4'].includes(tagName)) {
        // Handle headers for ordered content
        el.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(nested => nested.remove());
        const headerText = cleanText(el.textContent || '');
        
        if (isValidHeader(headerText)) {
          orderedContent.push({
            type: 'header',
            level: tagName,
            content: headerText
          });
        }
      } else if (tagName === 'p') {
        // Handle paragraphs for ordered content
        const text = cleanText(el.textContent || '');
        if (text.length >= 20) {
          orderedContent.push({
            type: 'text',
            content: text
          });
        }
      } else if (['div', 'article', 'section', 'main'].includes(tagName)) {
        // Handle other content containers for ordered content
        if (!el.querySelector('div, article, section, main, p')) {
          const text = cleanText(el.textContent || '');
          if (text.length >= 20) {
            orderedContent.push({
              type: 'text',
              content: text
            });
          }
        }
      }
    });

    // Calculate word count
    const allText = [...texts, ...Object.values(headers).flat()].join(' ');
    const wordCount = allText.split(/\s+/).filter(Boolean).length;

    return {
      headers,
      texts,
      word_count: wordCount,
      orderedContent
    };
  } catch (error) {
    console.error('Error parsing URL:', url, error);
    return {
      headers: { h1: [], h2: [], h3: [], h4: [] },
      texts: [],
      word_count: 0,
      orderedContent: []
    };
  }
}

// Special function for editor - preserves original document order
export async function parseUrlForEditor(url: string): Promise<ParsedContent> {
  try {
    console.log('Fetching URL for editor:', url);
    const html = await fetchWithRetry(url);
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Remove unwanted elements
    const unwantedSelectors = [
      'script', 'style', 'noscript', 'iframe', 'nav', 'footer', 
      'header', 'aside', '.menu', '.navigation', '.sidebar'
    ];
    
    unwantedSelectors.forEach(selector => {
      doc.querySelectorAll(selector).forEach(el => el.remove());
    });

    // Extract content in document order for editor
    const orderedContent: Array<{ type: 'header' | 'text', level?: string, content: string }> = [];
    const texts: string[] = [];
    const headers = {
      h1: [] as string[],
      h2: [] as string[],
      h3: [] as string[],
      h4: [] as string[]
    };

    // Get all content elements in document order
    const allElements = doc.querySelectorAll('h1, h2, h3, h4, p, div, article, section, main');
    
    allElements.forEach(el => {
      const tagName = el.tagName.toLowerCase();
      
      // Check if element is visible
      const style = window.getComputedStyle ? window.getComputedStyle(el) : null;
      if (style && (style.display === 'none' || style.visibility === 'hidden')) {
        return;
      }

      if (['h1', 'h2', 'h3', 'h4'].includes(tagName)) {
        // Handle headers
        el.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(nested => nested.remove());
        const headerText = cleanText(el.textContent || '');
        
        if (isValidHeader(headerText)) {
          headers[tagName as keyof typeof headers].push(headerText);
          orderedContent.push({
            type: 'header',
            level: tagName,
            content: headerText
          });
        }
      } else if (tagName === 'p') {
        // Handle paragraphs
        const text = cleanText(el.textContent || '');
        if (text.length >= 20) {
          texts.push(text);
          orderedContent.push({
            type: 'text',
            content: text
          });
        }
      } else if (['div', 'article', 'section', 'main'].includes(tagName)) {
        // Handle other content containers
        if (!el.querySelector('div, article, section, main, p')) {
          const text = cleanText(el.textContent || '');
          if (text.length >= 20) {
            texts.push(text);
            orderedContent.push({
              type: 'text',
              content: text
            });
          }
        }
      }
    });

    // Calculate word count
    const allText = [...texts, ...Object.values(headers).flat()].join(' ');
    const wordCount = allText.split(/\s+/).filter(Boolean).length;

    const result = {
      headers,
      texts,
      word_count: wordCount,
      orderedContent
    };

    console.log('parseUrlForEditor result:', result);
    console.log('orderedContent length:', orderedContent.length);
    
    return result;
  } catch (error) {
    console.error('Error parsing URL for editor:', url, error);
    return {
      headers: { h1: [], h2: [], h3: [], h4: [] },
      texts: [],
      word_count: 0,
      orderedContent: []
    };
  }
}

export async function parseCompetitorContent(
  competitors: { domain: string; url: string }[],
  ourUrl: string | null,
  onProgress?: (progress: string) => void
): Promise<any> {
  try {
    onProgress?.('Starting content analysis...');

    const contentAnalysis: {
      our_domain: ParsedContent;
      competitors: Array<ParsedContent & { domain: string; url: string }>;
    } = {
      our_domain: {
        headers: { h1: [], h2: [], h3: [], h4: [] },
        texts: [],
        word_count: 0,
        orderedContent: []
      },
      competitors: []
    };

    if (ourUrl) {
      onProgress?.(`Analyzing our content at ${ourUrl}...`);
      try {
        contentAnalysis.our_domain = await parseUrl(ourUrl);
      } catch (error) {
        console.error('Error parsing our URL:', error);
      }
    }

    for (let i = 0; i < competitors.length; i++) {
      const competitor = competitors[i];
      onProgress?.(`Analyzing competitor ${i + 1} of ${competitors.length}: ${competitor.domain}...`);
      
      try {
        const parsedContent = await parseUrl(competitor.url);
        
        if (parsedContent.word_count > 0 || 
            Object.values(parsedContent.headers).some(arr => arr.length > 0)) {
          contentAnalysis.competitors.push({
            domain: competitor.domain,
            url: competitor.url,
            ...parsedContent
          });
        }
      } catch (error) {
        console.error('Error parsing competitor URL:', competitor.url, error);
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    onProgress?.('Content analysis completed!');
    return contentAnalysis;

  } catch (error) {
    console.error('Error in parseCompetitorContent:', error);
    throw error;
  }
}